// Registro de Actividades de Tratamiento (RAT) — LOPDP Art. 47.
// Sistemas → Gestión de Autenticación → Registro de tratamiento. Solo admin (nivel>=5).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Download, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

interface Actividad {
  id: string; codigo: string; nombre: string; finalidad: string | null; base_legal: string | null;
  categorias_titulares: string | null; categorias_datos: string | null; datos_sensibles: boolean;
  destinatarios: string | null; transferencias_internacionales: string | null;
  plazo_conservacion: string | null; medidas_seguridad: string | null; sistema: string | null;
  responsable_area: string | null; es_activo: boolean;
}

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

const vacio = {
  id: '', codigo: '', nombre: '', finalidad: '', base_legal: '', categorias_titulares: '',
  categorias_datos: '', datos_sensibles: false, destinatarios: '', transferencias_internacionales: '',
  plazo_conservacion: '', medidas_seguridad: '', sistema: '', responsable_area: '', es_activo: true,
};
type Form = typeof vacio;

const CAMPOS_TEXTO: Array<{ k: keyof Form; label: string; help?: string }> = [
  { k: 'finalidad', label: 'Finalidad del tratamiento' },
  { k: 'base_legal', label: 'Base legal' },
  { k: 'categorias_titulares', label: 'Categorías de titulares', help: 'Pacientes, clientes, empleados…' },
  { k: 'categorias_datos', label: 'Categorías de datos' },
  { k: 'destinatarios', label: 'Destinatarios / encargados' },
  { k: 'transferencias_internacionales', label: 'Transferencias internacionales', help: 'Países y garantías (si las hay)' },
  { k: 'plazo_conservacion', label: 'Plazo de conservación' },
  { k: 'medidas_seguridad', label: 'Medidas de seguridad' },
];

export function RegistroTratamiento() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching, error } = useQuery<Actividad[]>({
    queryKey: ['rat'], queryFn: () => rpc<Actividad[]>('rat_listar'),
  });
  const [form, setForm] = useState<Form | null>(null);

  const guardar = useMutation({
    mutationFn: (f: Form) => rpc('rat_guardar', { _data: f }),
    onSuccess: () => { toast.success('Actividad guardada'); setForm(null); void qc.invalidateQueries({ queryKey: ['rat'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('rat_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Actividad eliminada'); void qc.invalidateQueries({ queryKey: ['rat'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const exportar = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `registro-actividades-tratamiento-${fechaLocal()}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (error) return <div className="p-6 text-sm text-urgente">{getErrorMessage(error)}</div>;

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" /> Registro de actividades de tratamiento</h1>
          <p className="text-sm text-muted-foreground">Inventario de los tratamientos de datos personales para demostrar cumplimiento (LOPDP Art. 47).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={!rows.length} onClick={exportar}><Download className="w-4 h-4 mr-1" /> Exportar</Button>
          <Button onClick={() => setForm({ ...vacio })}><Plus className="w-4 h-4 mr-1" /> Nueva actividad</Button>
        </div>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Actividad</th><th className="py-2 pr-3">Finalidad</th><th className="py-2 pr-3">Base legal</th>
              <th className="py-2 pr-3">Titulares</th><th className="py-2 pr-3">Sensibles</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin actividades registradas</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-b last:border-0 align-top hover:bg-muted/40">
                  <td className="py-2 pr-3"><div className="font-medium">{a.nombre}</div><div className="text-xs font-mono text-muted-foreground">{a.codigo}{a.sistema ? ` · ${a.sistema}` : ''}</div></td>
                  <td className="py-2 pr-3 max-w-xs text-muted-foreground">{a.finalidad}</td>
                  <td className="py-2 pr-3 max-w-[12rem] text-muted-foreground">{a.base_legal}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.categorias_titulares}</td>
                  <td className="py-2 pr-3">{a.datos_sensibles ? <span className="rounded bg-urgente-soft px-2 py-0.5 text-xs text-urgente">sí</span> : '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar"
                      onClick={() => setForm({ ...vacio, ...a, finalidad: a.finalidad ?? '', base_legal: a.base_legal ?? '', categorias_titulares: a.categorias_titulares ?? '', categorias_datos: a.categorias_datos ?? '', destinatarios: a.destinatarios ?? '', transferencias_internacionales: a.transferencias_internacionales ?? '', plazo_conservacion: a.plazo_conservacion ?? '', medidas_seguridad: a.medidas_seguridad ?? '', sistema: a.sistema ?? '', responsable_area: a.responsable_area ?? '' })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar la actividad "${a.nombre}"?`, { peligro: true })) eliminar.mutate(a.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {form && (
        <Modal title={form.id ? 'Editar actividad' : 'Nueva actividad de tratamiento'} onClose={() => setForm(null)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Sistema / módulo"><input className={fieldCls} value={form.sistema} onChange={(e) => setForm({ ...form, sistema: e.target.value })} /></Campo>
            </div>
            {CAMPOS_TEXTO.map(({ k, label, help }) => (
              <Campo key={k} label={label}>
                <textarea className={`${fieldCls} h-14`} value={form[k] as string} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                {help && <span className="text-xs text-muted-foreground">{help}</span>}
              </Campo>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Área/persona responsable"><input className={fieldCls} value={form.responsable_area} onChange={(e) => setForm({ ...form, responsable_area: e.target.value })} /></Campo>
              <div className="flex items-end gap-4 pb-2">
                <Switch checked={form.datos_sensibles} onChange={(v) => setForm({ ...form, datos_sensibles: v })} label="Incluye datos sensibles" />
                <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activa" />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate(form)}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
